import RefreshIcon from '@mui/icons-material/Refresh';
import { Chip, Tooltip, Typography } from '@mui/material';
import {
  GridActionsCellItem,
  GridColumns,
  GridRenderCellParams,
  GridRowParams
} from '@mui/x-data-grid-premium';
import DataGridCell from 'components/common/DataGridCell';
import { dateCell } from 'components/common/enhanced/cells/date-cell';
import toast from 'features/toast';
import { useTranslation } from 'react-i18next';
import { lmToSAPQueueService } from 'services/api';

export function LMToSAPQueueColumns(refresh: any): GridColumns {
  const { t } = useTranslation();

  const statusColor = (status: string): 'success' | 'error' | 'warning' => {
    if (status === 'IMPORTED') return 'success';
    if (status === 'ERROR') return 'error';
    return 'warning';
  };

  return [
    {
      field: 'tracking_id',
      headerName: t('entities.sap-settings.tracking_id'),
      width: 180
    },
    {
      field: 'type',
      headerName: t('entities.sap-settings.type'),
      width: 180,
      valueFormatter: (params) =>
        t(`entities.sap-settings.type_options.${params.value}`, {
          defaultValue: params.value
        })
    },
    {
      field: 'status',
      headerName: t('entities.sap-settings.status'),
      width: 120,
      renderCell: (params: GridRenderCellParams<string>) => (
        <Chip
          label={t(`entities.sap-settings.status_options.${params.value}`, {
            defaultValue: params.value
          })}
          color={statusColor(params.value)}
          size="small"
        />
      )
    },
    {
      field: 'description',
      headerName: t('entities.sap-settings.description'),
      flex: 1,
      renderCell: (params: GridRenderCellParams<string>) => (
        <DataGridCell content={params.value || ''} />
      )
    },
    {
      field: 'payload',
      headerName: t('entities.sap-settings.payload'),
      width: 220,
      sortable: false,
      renderCell: (params: GridRenderCellParams<unknown>) => {
        if (!params.value) {
          return <DataGridCell content="" />;
        }
        const fullPayload = JSON.stringify(params.value, null, 2);
        const shortPayload =
          fullPayload.length > 50 ? `${fullPayload.slice(0, 50)}...` : fullPayload;
        return (
          <Tooltip
            placement="top"
            arrow
            title={
              <Typography component="pre" sx={{ margin: 0, fontSize: '0.75rem' }}>
                {fullPayload}
              </Typography>
            }>
            <Typography noWrap variant="body2" sx={{ width: '100%' }}>
              {shortPayload}
            </Typography>
          </Tooltip>
        );
      }
    },
    {
      field: 'created_at',
      headerName: t('entities.sap-settings.created_at'),
      width: 140,
      renderCell: dateCell
    },
    {
      field: 'updated_at',
      headerName: t('entities.sap-settings.updated_at'),
      width: 140,
      renderCell: dateCell
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('global.actions'),
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          key={1}
          icon={<RefreshIcon />}
          label={t('entities.sap-settings.refresh')}
          onClick={() => {
            lmToSAPQueueService
              .pollStatus(params.row.tracking_id)
              .then(() => {
                toast.success(t('entities.sap-settings.refreshed'));
                if (refresh?.current) refresh.current();
              })
              .catch(() => undefined);
          }}
          showInMenu={false}
        />
      ],
      filterable: false
    }
  ];
}
